import type {Dispatch, GetState, ThunkAction} from 'src/reducers';
import type {AutomationWorkflowState} from 'src/reducers/automation-workflow';
import type {FrequencyLimit, Swimlane} from 'src/types/automation-workflow';
import type {GenericObject} from 'src/types/sense';
import type {CanvasNodeTypes} from 'src/types/automation';
import * as reduxApi from 'src/utils/redux-api-bff';
import {thunkify as flow} from 'src/utils/thunks';
import {key, fetching} from 'src/utils/redux';
import {
  parseAutomationWorkflow,
  normalizeAutomationWorkflow,
} from 'src/utils/automation-workflow';

import {parseSplits} from 'src/utils/split';
import {receiveSplits} from 'src/action-creators/split';


type UpdateAutomationWorkflowPayload = Partial<AutomationWorkflowState>;

export const RECEIVE_AUTOMATION_WORKFLOW = 'automation-workflow/receive';
export const UPDATE_AUTOMATION_WORKFLOW = 'automation-workflow/update';
export const RESET_AUTOMATION_WORKFLOW = 'automation-workflow/reset';
export const ADD_TEMP_NODE = 'automation-workflow/add-temp-node';
export const ADD_TEMP_TRANSITION = 'automation-workflow/add-temp-transition';
export const RESET_TEMP_NODES = 'automation-workflow/reset-temp-nodes';
export const ADD_TEMP_SPLIT = 'automation-workflow/add-temp-split';
export const DELETE_SPLIT = 'automation-workflow/delete-split';

type UpdateAutomationWorkflowAction = {
  type: 'automation-workflow/update';
  payload: UpdateAutomationWorkflowPayload;
};
type ResetAutomationWorkflowAction = {
  type: 'automation-workflow/reset';
  payload?: AutomationWorkflowState;
};
type ReceiveAutomationWorkflowAction = {
  type: 'automation-workflow/receive';
  payload: AutomationWorkflowState;
};
type AddTempNodeAction = {
  type: 'automation-workflow/add-temp-node';
  payload: {
    nodeType: CanvasNodeTypes;
    swimlaneId: string;
    sourceNodeId: string;
    transitionId: number | null | undefined;
  };
};

type AddTempTransitionAction = {
  type: 'automation-workflow/add-temp-transition';
  payload: {
    sourceNodeId: string;
    swimlaneId: string;
    position: 'left' | 'right';
  };
};

type ResetTempNodesAction = {
  type: 'automation-workflow/reset-temp-nodes';
};

type AddTempSplitAction = {
  type: 'automation-workflow/add-temp-split';
  payload: {
    swid: string;
    nodeId: string;
    tid: string;
    position: number;
  };
};

type DeleteSplitAction = {
  type: 'automation-workflow/delete-split';
  payload: {
    swid: string;
    nodeId: string;
    position: number;
  };
};

export type AutomationWorkflowAction =
  | UpdateAutomationWorkflowAction
  | ResetAutomationWorkflowAction
  | ReceiveAutomationWorkflowAction
  | AddTempNodeAction
  | AddTempTransitionAction
  | ResetTempNodesAction
  | AddTempSplitAction
  | DeleteSplitAction;

export const updateAutomationWorkflow = (
  payload: UpdateAutomationWorkflowPayload,
): UpdateAutomationWorkflowAction => ({
  type: UPDATE_AUTOMATION_WORKFLOW,
  payload,
});
//add a new journey with provided or initial state
export const resetAutomationWorkflow = (
  payload?: AutomationWorkflowState,
): ResetAutomationWorkflowAction => ({
  type: RESET_AUTOMATION_WORKFLOW,
  payload,
});
// put data received from api into store
export const receiveAutomationWorkflow = (
  payload: AutomationWorkflowState,
): ReceiveAutomationWorkflowAction => ({
  type: RECEIVE_AUTOMATION_WORKFLOW,
  payload,
});
//add a client side node
export const addTempNode = (payload: {
  swimlaneId: string;
  nodeType: CanvasNodeTypes;
  sourceNodeId: string;
  transitionId: number | null | undefined;
}): AddTempNodeAction => ({
  type: ADD_TEMP_NODE,
  payload,
});

export const addTempTransition = (payload: {
  sourceNodeId: string;
  swimlaneId: string;
  position: 'left' | 'right';
}): AddTempTransitionAction => ({
  type: ADD_TEMP_TRANSITION,
  payload,
});

export const resetTempNodes = (): ResetTempNodesAction => ({
  type: RESET_TEMP_NODES,
});

export const getAutomationWorkflow: (
  automationWorkflowId: string,
  hfNodeId?: string | null | undefined,
) => ThunkAction<unknown> = flow(
  key(
    // @ts-expect-error - TS7006 - Parameter 'automationWorkflowId' implicitly has an 'any' type. | TS7006 - Parameter 'hfNodeId' implicitly has an 'any' type.
    (automationWorkflowId, hfNodeId) =>
      `getAutomationWorkflow-${automationWorkflowId}-${hfNodeId}`,
  ),
  fetching(),
)((automationWorkflowId, hfNodeId) => async (dispatch: Dispatch) => {
  const swimlaneURL = hfNodeId
    ? `workflow/composite/validate-delete?node_id=${hfNodeId}`
    : `workflow/${automationWorkflowId}/swimlanes`;
  try {
    const [automationWorkflow, swimlanes] = await Promise.all([
      dispatch(reduxApi.get(`workflow/${automationWorkflowId}`)),
      dispatch(reduxApi.get(swimlaneURL)),
    ]).catch((ex) => {
      throw ex;
    });

    const automationWorkflowData = {
      ...automationWorkflow,
      ...swimlanes,
    } as const;
    //we need to sync the split store whenever we receive workflows.
    await dispatch(receiveSplits(parseSplits(swimlanes.swimlanes)));
    return dispatch(
      receiveAutomationWorkflow(
        parseAutomationWorkflow(automationWorkflowData),
      ),
    );
  } catch (ex: any) {
    throw ex;
  }
});

export const createAutomationWorkflow: () => ThunkAction<unknown> =
  () => (dispatch: Dispatch, getState: GetState) => {
    const automationWorkflow = getState().automationWorkflow;
    return dispatch(
      reduxApi.post(
        `workflow`,
        normalizeAutomationWorkflow(automationWorkflow),
      ),
    );
  };

//for now, only updating name and brand_id is allowed for workflows
export const saveAutomationWorkflow: (payload: {
  name?: string;
  brand_id?: string;
  frequency_limit?: FrequencyLimit;
  blackout_window_id?: number;
}) => ThunkAction<unknown> =
  (payload) => (dispatch: Dispatch, getState: GetState) => {
    const {id} = getState().automationWorkflow;
    return dispatch(reduxApi.put(`workflow/${id}`, payload)).then(({name}) =>
      dispatch(updateAutomationWorkflow({name})),
    );
  };

export const activateAutomationWorkflow: (id: string) => ThunkAction<unknown> =
  (id) => (dispatch: Dispatch) => {
    return dispatch(reduxApi.post(`workflow/${id}/activate`)).then(() =>
      dispatch(getAutomationWorkflow(id)),
    );
  };

export const deactivateAutomationWorkflow: (
  id: string,
) => ThunkAction<unknown> = (id) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.post(`workflow/${id}/deactivate`)).then(() =>
    dispatch(getAutomationWorkflow(id)),
  );
};

export const archiveAutomationWorkflow: (id: string) => ThunkAction<unknown> =
  (id) => (dispatch: Dispatch) => {
    return dispatch(reduxApi.post(`workflow/${id}/archive`)).then(() =>
      dispatch(getAutomationWorkflow(id)),
    );
  };

export const unarchiveAutomationWorkflow: (
  id: string,
) => ThunkAction<unknown> = (id) => (dispatch: Dispatch) => {
  return dispatch(reduxApi.post(`workflow/${id}/unarchive`)).then(() =>
    dispatch(getAutomationWorkflow(id)),
  );
};

export const deleteAutomationWorkflow: (id: string) => ThunkAction<unknown> =
  (id) => (dispatch: Dispatch) => {
    return dispatch(reduxApi.del(`workflow/${id}`));
  };

export const starAutomationWorkflow: (id: string) => ThunkAction<unknown> =
  (id) => (dispatch: Dispatch) => {
    //optimistically update the store
    dispatch(updateAutomationWorkflow({starred: true}));
    return dispatch(reduxApi.post(`workflow/star`, {workflow_ids: [id]})).catch(
      (ex) => {
        //revert optimistic update
        dispatch(updateAutomationWorkflow({starred: false}));
        throw ex;
      },
    );
  };

export const unstarAutomationWorkflow: (id: string) => ThunkAction<unknown> =
  (id) => (dispatch: Dispatch) => {
    //optimistically update the store
    dispatch(updateAutomationWorkflow({starred: false}));
    return dispatch(reduxApi.post(`workflow/${id}/unstar`)).catch((ex) => {
      //revert optimistic update
      dispatch(updateAutomationWorkflow({starred: true}));
      throw ex;
    });
  };

export const deleteAutomationWorkflowNode: (
  nodeId: string,
  automationWorkflowId: string,
  nextNodeId?: string | null | undefined,
) => ThunkAction<unknown> =
  (nodeId, automationWorkflowId, nextNodeId) => (dispatch: Dispatch) => {
    const path = nextNodeId
      ? `node/composite/${nodeId}?next_node_id=${nextNodeId}`
      : `node/composite/${nodeId}`;
    return dispatch(reduxApi.del(path)).then(() =>
      dispatch(getAutomationWorkflow(automationWorkflowId)),
    );
  };

export const deleteAllNodesBelowNode: (
  nodeId: string,
  automationWorkflowId: string,
) => ThunkAction<unknown> =
  (nodeId, automationWorkflowId) => (dispatch: Dispatch) => {
    return dispatch(reduxApi.del(`node/composite/delete-all/${nodeId}`)).then(
      () => dispatch(getAutomationWorkflow(automationWorkflowId)),
    );
  };

export const deleteAutomationWorkflowTransition: (
  nodeId: string,
  nextNodeId: string,
  automationWorkflowId: string,
) => ThunkAction<unknown> =
  (nodeId, nextNodeId, automationWorkflowId) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.del(
        `node/transition?previous_node=${nodeId}&next_node=${nextNodeId}`,
      ),
    ).then(() => dispatch(getAutomationWorkflow(automationWorkflowId)));
  };

export const modifySecurityGroupAccess: (
  automationWorkflowId: string,
  securityGroupId: string,
  access: boolean,
) => ThunkAction<unknown> =
  (automationWorkflowId, securityGroupId, access) => (dispatch: Dispatch) => {
    return dispatch(
      reduxApi.post(`workflow/${automationWorkflowId}/access`, {
        security_group_id: securityGroupId,
        access,
      }),
    ).then(() => dispatch(getAutomationWorkflow(automationWorkflowId)));
  };

export const deleteModule: (module: {id: number}) => ThunkAction<unknown> =
  (module) => (dispatch: Dispatch) => {
    const {id} = module;
    return dispatch(reduxApi.del(`task/${id}`));
  };
export const deleteNode: (nodeId: string) => ThunkAction<unknown> =
  (nodeId) => (dispatch: Dispatch) => {
    return dispatch(reduxApi.del(`node/composite/${nodeId}`));
  };

export const addTempSplit = (payload: {
  swid: string;
  nodeId: string;
  tid: string;
  position: number;
}): AddTempSplitAction => ({
  type: ADD_TEMP_SPLIT,
  payload,
});

export const deleteSplit = (payload: {
  swid: string;
  nodeId: string;
  position: number;
}): DeleteSplitAction => ({
  type: DELETE_SPLIT,
  payload,
});

export const showDeleteIntermediateModal: (
  nodeId: string,
) => ThunkAction<unknown> =
  (nodeId): ThunkAction<unknown> =>
  async (dispatch, Dispatch) => {
    try {
      const {valid_deletion} = await dispatch(
        reduxApi.get(`workflow/composite/validate-delete`, {node_id: nodeId}),
      );
      // nocommit(vish): flip it before commit
      return !valid_deletion;
    } catch (ex: any) {
      throw ex;
    }
  };

export const getAutomationWorkflowSwimlanes: (
  automationWorkflowId: string,
) => ThunkAction<Array<Swimlane>> =
  (automationWorkflowId) => async (dispatch: Dispatch) => {
    try {
      const swimlanes: {
        swimlanes: Array<Swimlane>;
      } = await dispatch(
        reduxApi.get(`workflow/${automationWorkflowId}/swimlanes`),
      );
      return swimlanes.swimlanes ?? [];
    } catch (ex: any) {
      throw ex;
    }
  };

export const createNode: (
  node_type: string,
  body: Record<any, any>,
  canvasParams: {
    sid: string;
    tid: string;
    swid: string;
    workflow_id: string;
  },
) => ThunkAction<unknown> =
  (node_type, body, canvasParams) => async (dispatch: Dispatch) => {
    const {sid, tid, swid: swimlane_id, workflow_id} = canvasParams;
    const apiPayload = {
      swimlane_id,
      workflow_id,
      body,
    } as const;

    return await dispatch(
      reduxApi.post(
        `node/${node_type}?next_node=${tid}&previous_node=${sid}`,
        apiPayload,
      ),
    );
  };
export const updateNode: (
  node_type: string,
  body: Record<any, any>,
  canvasParams: {
    swid: string;
    workflow_id: string;
    node_id: string;
  },
) => ThunkAction<unknown> =
  (node_type, body, canvasParams) => async (dispatch: Dispatch) => {
    const {swid: swimlane_id, workflow_id, node_id} = canvasParams;
    const apiPayload = {
      swimlane_id,
      workflow_id,
      //TODO:(diwakersurya) change this later
      composite_id: node_id,
      body,
    } as const;
    return await dispatch(reduxApi.put(`node/${node_type}`, apiPayload));
  };

export const addNodeTask: (
  task: GenericObject,
  canvasParams: {
    node_id: string;
  },
) => ThunkAction<unknown> =
  (task, canvasParams) => async (dispatch: Dispatch) => {
    const {node_id} = canvasParams;
    //use the taskNodeId here in this call
    const taskCreationResult = await dispatch(
      reduxApi.post(`node/add-task`, task, {
        node_id,
      }),
    );
    return taskCreationResult;
  };

// Copy Automation Workflow
export const copyAutomationWorkflow =
  ({
    originalWorkflowId,
    newName,
    categoryId,
    copyAudienceLists,
  }: {
    originalWorkflowId: string;
    newName: string;
    categoryId: string | null | undefined;
    copyAudienceLists: boolean;
  }): ThunkAction<unknown> =>
  async (dispatch: Dispatch) => {
    try {
      const response = await dispatch(
        reduxApi.post(`workflow/copy`, {
          sourceWorkflowId: originalWorkflowId,
          newWorkflowName: newName,
          targetCategoryId: categoryId,
          copyLists: copyAudienceLists,
        }),
      );
      return response;
    } catch (err: any) {
      throw err;
    }
  };
